import { effect, Signal, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { queryOptions, QueryObserver } from '@tanstack/react-query';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { compareKey, stringifyKey, TableClient } from '@thinkalpha/table-client';
import type { Bounds, RawClient, RowUpdate, SortModel } from '@thinkalpha/table-client';
import { deferred } from '@thinkalpha/ui-core/deferred';
import { shortid } from '@thinkalpha/ui-core/shortid';
import { isEmpty } from 'es-toolkit/compat';
import { jwtDecode } from 'jwt-decode';
import {
    BehaviorSubject,
    concat,
    connectable,
    distinctUntilChanged,
    filter,
    from,
    map,
    NEVER,
    of,
    ReplaySubject,
    skip,
    switchMap,
    tap,
} from 'rxjs';
import type { Connectable, Observable, ObservedValueOf } from 'rxjs';
import { getSnapshot } from 'src/api/getTableSnapshot';
import type { TableStatus } from 'src/components/table-view/model';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { getSpecialTableQueryKeys, NEW_TABLE_RESET } from 'src/models/TableModel';
import type { TableCreationOptions, TableModel, SignalUpdate, OptimizedTableFilter } from 'src/models/TableModel';
import type { FullKeyType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

/**
 * Key used to mark query results that came from the optimization path.
 * When the wrapped queryFn returns an optimized result (sort/filter amendment),
 * it wraps the post-select result with this key so the wrapped select function
 * can detect it and bypass transformation.
 *
 * Note: We use a string key instead of a Symbol because React Query's structural
 * sharing strips Symbols when cloning query results.
 */
const OptimizedQueryResult = '__optimizedQueryResult__' as const;

interface OptimizedResultWrapper {
    [OptimizedQueryResult]: true;
    result: FullKeyType;
}

function isOptimizedResult(value: unknown): value is OptimizedResultWrapper {
    return typeof value === 'object' && value !== null && OptimizedQueryResult in value;
}

/**
 * Internal query options type that includes OptimizedResultWrapper.
 * The wrapper is used internally to bypass select transformation for optimized results.
 */
type InternalQueryOptions = QueryObserverOptions<
    FullKeyType | OptimizedResultWrapper | undefined,
    any,
    FullKeyType | undefined
>;

interface TableState {
    columns: OutputColumnWithMetadata[];
    error: Error | undefined;
    queryOptions: InternalQueryOptions;
    tableCreationResult: FullKeyType;
    isAmendableSortTableCreationResult: boolean;
    isAmendableFilterTableCreationResult: boolean;
    tableClient: TableClient | null;
    proxyStatus: TableStatus;
    tableStatus: TableStatus;
    serverUUID: string | null;
}

type NonNullFullKeyType = Exclude<FullKeyType, null>;

type TableStateAction =
    | { type: 'clearTableClient' }
    | { type: 'enterErroredState'; error: Error }
    | { type: 'updateProxyConnectionStatus'; status: TableStatus }
    | { type: 'updateQueryOptions'; queryOptions: InternalQueryOptions }
    | { type: 'updateTableConnectionStatus'; status: TableStatus }
    | { type: 'updateServerUUID'; serverUUID: string }
    | {
          type: 'useNewTableCreationResult';
          tableCreationResult: NonNullFullKeyType;
          rawClient: RawClient;
          logger: ReactBindings['Logger'];
      };

const DEFAULT_THROTTLE_MS = 1;

const checkIfAmendAllowedInTableCookie = (tableCookie: string | undefined, type: 'view' | 'index'): boolean => {
    if (!tableCookie) {
        return false;
    }

    try {
        const decoded = jwtDecode(tableCookie) as { permitsAmend?: boolean | 'view' | 'index' };
        return decoded.permitsAmend === true || decoded.permitsAmend === type;
    } catch {
        return false;
    }
};

const tableStateReducer = (state: TableState, action: TableStateAction): TableState => {
    switch (action.type) {
        case 'clearTableClient':
            return {
                ...state,
                columns: [],
                error: undefined,
                isAmendableSortTableCreationResult: false,
                isAmendableFilterTableCreationResult: false,
                tableClient: null,
                tableCreationResult: null,
                serverUUID: null,
                tableStatus: 'uninitialized',
            };
        case 'enterErroredState':
            return {
                ...state,
                columns: [],
                error: action.error,
                isAmendableSortTableCreationResult: false,
                isAmendableFilterTableCreationResult: false,
                tableClient: null,
                tableCreationResult: null,
                serverUUID: null,
            };
        case 'useNewTableCreationResult': {
            const { rawClient, tableCreationResult, logger } = action;

            // The key may be the same if the user is sorting and the existing table is reconfigured
            if (compareKey(state.tableClient?.key, tableCreationResult.key)) {
                // TODO: We will set the fetch mode when the proxy supports diffs on X suffix
                //if (state.tableClient && state.tableClient.fetchMode !== tableCreationResult.fetchMode) {
                //    state.tableClient.fetchMode = tableCreationResult.fetchMode;
                //}
                return {
                    ...state,
                    columns: 'columns' in tableCreationResult ? tableCreationResult.columns : [],
                };
            }

            const tableClient = new TableClient(rawClient, stringifyKey(tableCreationResult.key), logger);
            tableClient.accessCookie = tableCreationResult.tableCookie;
            // TODO: We will set the fetch mode when the proxy supports diffs on X suffix
            //tableClient.fetchMode = tableCreationResult.fetchMode;
            return {
                ...state,
                columns: 'columns' in tableCreationResult ? tableCreationResult.columns : [],
                error: undefined,
                isAmendableSortTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'index',
                ),
                isAmendableFilterTableCreationResult: checkIfAmendAllowedInTableCookie(
                    tableCreationResult.tableCookie,
                    'view',
                ),
                tableClient,
                tableCreationResult,
                // Reset server UUID when table changes - we'll get a new one from the observable
                serverUUID: null,
            };
        }
        case 'updateProxyConnectionStatus':
            if (action.status === state.proxyStatus) {
                return state;
            }
            return {
                ...state,
                proxyStatus: action.status,
            };
        case 'updateQueryOptions':
            return {
                ...state,
                queryOptions: action.queryOptions,
            };
        case 'updateTableConnectionStatus':
            if (action.status === state.tableStatus) {
                return state;
            }
            return {
                ...state,
                tableStatus: action.status,
            };
        case 'updateServerUUID':
            if (action.serverUUID === state.serverUUID) {
                return state;
            }
            return {
                ...state,
                serverUUID: action.serverUUID,
            };
        default:
            return state;
    }
};

@injectable()
export class TableModelImpl extends ReactiveInjectable implements TableModel {
    #bounds!: Bounds;

    get bounds() {
        return this.#bounds;
    }

    #columnByIdMap = new Map<string, OutputColumnWithMetadata>();

    #tableClientQueryOptionsMap = new WeakMap<TableClient, InternalQueryOptions>();

    #queryOptionsSignal!: Signal<QueryObserverOptions<any, any, FullKeyType | undefined>>;

    get columns() {
        return this.#state.columns;
    }

    #columnsSignal: Signal<OutputColumnWithMetadata[]> = signal([]);
    get columnsSignal(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#columnsSignal;
    }

    #countConnected$: Connectable<number>;
    #count$: Observable<number>;

    get count$() {
        return this.#count$;
    }

    #progressConnected$: Connectable<number | undefined>;
    #progress$: Observable<number | undefined>;
    #progressSignal = signal<number | undefined>(undefined);

    get progress$() {
        return this.#progress$;
    }

    get progress() {
        return this.#progressSignal;
    }

    #connectionStatusSignal = signal<TableStatus>('uninitialized');

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#connectionStatusSignal;
    }

    #failureCountSignal = signal<number>(0);

    get failureCount(): ReadonlySignal<number> {
        return this.#failureCountSignal;
    }

    #keySignal = signal<string>('');

    get keySignal(): ReadonlySignal<string> {
        return this.#keySignal;
    }

    // Track the last query key that resulted in a successful table creation
    // Used by the wrapped queryFn to determine if only sort/filter changed
    #lastSuccessfulQueryKey: readonly unknown[] | undefined;

    /**
     * Wraps a queryFn with optimization logic that can bypass network requests
     * when only the sort model and/or filter changes and the existing table supports amending.
     *
     * This approach ensures the QueryObserver always has the correct query options,
     * which is important for reconnect scenarios where the QueryObserver needs to
     * refetch with the latest configuration.
     */
    #wrapQueryFn(
        originalQueryFn: QueryObserverOptions<FullKeyType | undefined>['queryFn'],
        newQueryKey: readonly unknown[] | undefined,
    ): InternalQueryOptions['queryFn'] {
        // queryFn can be undefined or a skipToken symbol - only wrap actual functions
        if (!originalQueryFn || typeof originalQueryFn !== 'function') {
            return originalQueryFn;
        }

        const originalFn = originalQueryFn;
        return async (context) => {
            const currentTableClient = this.#state.tableClient;
            const currentTableCreationResult = this.#state.tableCreationResult;
            const lastQueryKey = this.#lastSuccessfulQueryKey;

            // Check if we can optimize (sort/filter-only change on an amendable table)
            if (currentTableClient && currentTableCreationResult && lastQueryKey && newQueryKey) {
                const optimization = this.#checkOptimization(lastQueryKey, newQueryKey);

                if (optimization.canOptimize) {
                    if (TRACE) {
                        this.#log.trace('Optimizing via wrapped queryFn', {
                            sortChanged: optimization.sortChanged,
                            filterChanged: optimization.filterChanged,
                            newSortModel: optimization.newSortModel,
                            newFilter: optimization.newFilter,
                        });
                    }

                    // Apply sort if it changed
                    if (optimization.sortChanged) {
                        const sort: SortModel = isEmpty(optimization.newSortModel) ? {} : optimization.newSortModel;
                        currentTableClient.sort(sort, v4());
                    }

                    // Apply filter if it changed
                    if (optimization.filterChanged) {
                        if (!optimization.newFilter) {
                            // Clear filter
                            currentTableClient.filter('', v4(), false);
                        } else {
                            currentTableClient.filter(
                                optimization.newFilter.filter,
                                v4(),
                                optimization.newFilter.type === 'raw-formula',
                            );
                        }
                    }

                    // Update tracking and return existing result (no network call)
                    this.#lastSuccessfulQueryKey = newQueryKey;

                    if (TRACE) {
                        this.#log.trace('returning current TCR (optimized)', {
                            newQueryKey,
                            currentTableCreationResult,
                            state: this.#state,
                        });
                    }

                    // Wrap the result so the select function knows to bypass transformation.
                    // The post-select tableCreationResult is already what we want, so we mark it
                    // to prevent select from running on it again.
                    return {
                        [OptimizedQueryResult]: true,
                        result: currentTableCreationResult,
                    } satisfies OptimizedResultWrapper;
                }
            }

            // Can't optimize - go to network
            if (TRACE) {
                this.#log.trace('Cannot optimize, calling original queryFn');
            }
            const result = await originalFn(context);

            // Track successful query key for future optimization comparisons
            if (result) {
                this.#lastSuccessfulQueryKey = newQueryKey;
            }

            return result;
        };
    }

    /**
     * Checks if we can optimize by updating sort/filter on the existing table
     * instead of making a network request.
     */
    #checkOptimization(
        oldKey: readonly unknown[],
        newKey: readonly unknown[],
    ): {
        canOptimize: boolean;
        sortChanged: boolean;
        filterChanged: boolean;
        newSortModel: SortModel;
        newFilter: OptimizedTableFilter | undefined;
    } {
        const { sort: oldSortModel, filter: oldFilter, rest: oldUnspecialKey } = getSpecialTableQueryKeys(oldKey);
        const { sort: newSortModel, filter: newFilter, rest: newUnspecialKey } = getSpecialTableQueryKeys(newKey);

        // Check if only sort/filter changed (everything else must be identical)
        const keysMatch =
            oldUnspecialKey.length === newUnspecialKey.length &&
            oldUnspecialKey.every((item, index) => {
                return JSON.stringify(item) === JSON.stringify(newUnspecialKey[index]);
            });

        if (!keysMatch) {
            return {
                canOptimize: false,
                sortChanged: false,
                filterChanged: false,
                newSortModel: newSortModel || {},
                newFilter,
            };
        }

        const sortChanged = JSON.stringify(oldSortModel) !== JSON.stringify(newSortModel);
        let filterChanged = JSON.stringify(oldFilter) !== JSON.stringify(newFilter);

        // Don't optimize formula type filters
        if (filterChanged && newFilter?.type === 'formula') {
            if (TRACE) {
                this.#log.trace('Skipping filter optimization for formula type');
            }
            filterChanged = false;
        }

        // Determine if we can actually optimize based on permissions
        const canOptimizeSort = sortChanged && this.#state.isAmendableSortTableCreationResult;
        const canOptimizeFilter = filterChanged && this.#state.isAmendableFilterTableCreationResult;

        // We can optimize if:
        // - Only sort changed and we can amend sort
        // - Only filter changed and we can amend filter
        // - Both changed and we can amend both
        let canOptimize = false;
        if (sortChanged && filterChanged) {
            canOptimize = canOptimizeSort && canOptimizeFilter;
        } else if (sortChanged) {
            canOptimize = canOptimizeSort;
        } else if (filterChanged) {
            canOptimize = canOptimizeFilter;
        }

        if (TRACE && !canOptimize && (sortChanged || filterChanged)) {
            this.#log.trace('Skipping optimization due to permission mismatch', {
                sortChanged,
                filterChanged,
                isAmendableSortTableCreationResult: this.#state.isAmendableSortTableCreationResult,
                isAmendableFilterTableCreationResult: this.#state.isAmendableFilterTableCreationResult,
            });
        }

        return {
            canOptimize,
            sortChanged: canOptimize && sortChanged,
            filterChanged: canOptimize && filterChanged,
            newSortModel: newSortModel || {},
            newFilter,
        };
    }

    /**
     * Wraps a select function to detect optimized results and bypass transformation.
     */
    #wrapSelect(
        originalSelect: QueryObserverOptions<FullKeyType | undefined>['select'],
    ): InternalQueryOptions['select'] {
        const select = originalSelect ?? ((data: FullKeyType | undefined) => data);
        return (data) => {
            if (isOptimizedResult(data)) {
                return data.result;
            }
            return select(data);
        };
    }

    /**
     * Creates query options with a wrapped queryFn for optimization.
     */
    #createOptimizedQueryOptions(options: QueryObserverOptions<FullKeyType | undefined>): InternalQueryOptions {
        return {
            ...options,
            queryFn: this.#wrapQueryFn(options.queryFn, options.queryKey),
            select: this.#wrapSelect(options.select),
        };
    }

    get latestCookie(): string | undefined {
        if (!this.#state.tableClient) return undefined;
        return this.#state.tableClient.accessCookie;
    }

    #updateConnectionStatusSignal() {
        const { tableStatus, proxyStatus } = this.#state;
        let newStatus: TableStatus = 'uninitialized';

        if (tableStatus === 'uninitialized' || proxyStatus === 'uninitialized') {
            newStatus = 'uninitialized';
        } else if (tableStatus === 'disconnected' || proxyStatus === 'disconnected') {
            newStatus = 'disconnected';
        } else if (tableStatus === 'connecting' || proxyStatus === 'connecting') {
            newStatus = 'connecting';
        } else if (tableStatus === 'connected' && proxyStatus === 'connected') {
            newStatus = 'connected';
        }

        if (this.#connectionStatusSignal.value !== newStatus) {
            this.#connectionStatusSignal.value = newStatus;
        }
    }

    constructor(
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('ProxyClientService') private proxyClientService: ReactBindings['ProxyClientService'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('Store') private store: ReactBindings['Store'],
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#id = shortid();
        this.#log = logger.getSubLogger({ name: `TableModel:${this.#id}` }, { component: 'table' });

        this.#updateConnectionStatusSignal();

        const wasPreviouslyConnected = false;

        const subscription = this.proxyClientService.client.connectionStatus$.subscribe((status) => {
            const previousStatus = this.#state.proxyStatus;
            if (status !== previousStatus) {
                this.#log.debug({ message: 'Proxy connection status changed', previousStatus, status });
            }

            this.#takeStateAction({ type: 'updateProxyConnectionStatus', status });

            if (wasPreviouslyConnected && status === 'disconnected') {
                this.#log.debug({ message: 'Proxy connection was dropped, refetching TCR' });
                this.#query?.refetch();
            }
        });
        this.disposableStack.use(subscription);

        // Connect to the TableClient's tsId$ observable manually
        const sub = from(this)
            .pipe(
                map(() => this.tableClient),
                distinctUntilChanged(),
                tap((tableClient) => {
                    if (tableClient && tableClient.tsId$ && 'connect' in tableClient.tsId$) {
                        // Assuming tsId$ is a connectable but not connected
                        const connectableObservable = tableClient.tsId$ as any;
                        if (typeof connectableObservable.connect === 'function') {
                            this.disposableStack.use(connectableObservable.connect());
                            if (TRACE) {
                                this.#log.trace({ message: 'Connected tsId$ observable' });
                            }
                        }
                    }
                }),
                switchMap((tableClient) => {
                    if (!tableClient) {
                        if (TRACE) {
                            this.#log.trace({
                                message: 'No TableClient available for server UUID tracking',
                            });
                        }
                        return NEVER;
                    }

                    if (TRACE) {
                        this.#log.trace({ message: 'Setting up tsId$ subscription for server UUID' });
                    }

                    return tableClient.tsId$;
                }),
            )
            .subscribe((tsId) => {
                if (tsId) {
                    if (TRACE) {
                        this.#log.trace({
                            message: 'Received server UUID from tsId$',
                            tsId,
                        });
                    }
                    this.#takeStateAction({ type: 'updateServerUUID', serverUUID: tsId });
                } else if (TRACE) {
                    this.#log.trace({ message: 'Empty tsId received' });
                }
            });
        this.disposableStack.use(sub);

        // Subscribe to end-of-data events from TableClient
        const endOfDataSub = from(this)
            .pipe(
                map(() => this.tableClient),
                distinctUntilChanged(),
                switchMap((tableClient) => {
                    if (!tableClient) return NEVER;
                    return tableClient.endOfData$;
                }),
            )
            .subscribe(() => {
                this.#log.debug({ message: 'End of data received, refetching TCR' });
                this.#query?.refetch();
            });
        this.disposableStack.use(endOfDataSub);

        {
            this.disposableStack.use(
                from(this)
                    .pipe(
                        map(() => this.tableClient),
                        distinctUntilChanged(),
                        switchMap((tc) => concat(of(NEW_TABLE_RESET), tc ? tc.update$ : NEVER)),
                    )
                    .subscribe(this.#updateConnected$),
            );

            this.#updatesConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) => concat(of(NEW_TABLE_RESET), tc ? tc.updates$ : NEVER)),
                ),
                { connector: () => new ReplaySubject(1) },
            );

            this.disposableStack.use(this.#updatesConnected$.connect());

            this.#update$ = this.#updateConnected$.pipe(filter((update) => update !== NEW_TABLE_RESET));
            this.#updates$ = this.#updatesConnected$.pipe(filter((update) => update !== NEW_TABLE_RESET));
        }

        {
            this.#countConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) => concat(of(NEW_TABLE_RESET as unknown as number), tc ? tc.rowCount$ : NEVER)),
                ),
                { connector: () => new ReplaySubject(1) },
            );
            this.disposableStack.use(this.#countConnected$.connect());

            this.#count$ = this.#countConnected$.pipe(map((count) => ((count as any) === NEW_TABLE_RESET ? 0 : count)));
        }

        {
            this.#progressConnected$ = connectable(
                from(this).pipe(
                    map(() => this.tableClient),
                    distinctUntilChanged(),
                    switchMap((tc) =>
                        concat(
                            of(NEW_TABLE_RESET as unknown as number | undefined),
                            tc ? tc.progressUpdate$.pipe(map((update) => update.percentage)) : NEVER,
                        ),
                    ),
                ),
                { connector: () => new ReplaySubject(1) },
            );
            this.disposableStack.use(this.#progressConnected$.connect());

            this.#progress$ = this.#progressConnected$.pipe(
                map((progress) => ((progress as any) === NEW_TABLE_RESET ? undefined : progress)),
            );

            let lastProgressWarningTime: number | undefined;
            this.disposableStack.use(
                this.#progress$.subscribe((progress) => {
                    if (TRACE) {
                        this.#log.trace({
                            message: 'Table construction progress update',
                            progress,
                            tableKey: this.#state.tableClient?.key,
                        });
                    }

                    if (progress !== undefined && progress < 100) {
                        const now = performance.now();
                        if (lastProgressWarningTime === undefined) {
                            // First progress message, just record the time
                            lastProgressWarningTime = now;
                        } else if (now - lastProgressWarningTime >= 5000) {
                            lastProgressWarningTime = now;
                            this.#log.warn({
                                message: 'Table construction still in progress after 5 seconds',
                                progress,
                                tableKey: this.#state.tableClient?.key,
                            });
                        }
                    } else if (progress === 100 && lastProgressWarningTime !== undefined) {
                        this.#log.info({
                            message: 'Table construction complete',
                            tableKey: this.#state.tableClient?.key,
                        });
                        lastProgressWarningTime = undefined;
                    }

                    if (this.#progressSignal.value !== progress) {
                        this.#progressSignal.value = progress;
                    }
                }),
            );
        }
    }

    destroy() {
        this.#takeStateAction({ type: 'clearTableClient' });
    }

    refetch() {
        if (TRACE) {
            this.#log.trace({ message: 'refetch() called manually' });
        }
        if (this.#query) {
            // Tear down the existing TableClient immediately so the UI shows
            // 'connecting' right away rather than keeping stale data visible.
            this.#takeStateAction({ type: 'clearTableClient' });
            // Reset the optimization tracking so the next fetch goes to the network.
            this.#lastSuccessfulQueryKey = undefined;
            // Reset the query cache and trigger a new fetch.
            this.queryClient.resetQueries({
                queryKey: this.#state.queryOptions.queryKey,
                exact: true,
            });
        }
    }

    get error() {
        return this.#state.error;
    }

    getColumnById(columnId: string): OutputColumnWithMetadata | null {
        return this.#columnByIdMap.get(columnId) ?? null;
    }

    getColumnUpdate$(columnId: string) {
        // this.#log.trace({ message: 'TableClient is not null, returning update$', columnId });
        return this.update$.pipe(map((update) => update[columnId]));
    }

    init(tableCreationOptions: TableCreationOptions) {
        if (TRACE) {
            this.#log.trace({
                message: 'init() called',
                isSignal: tableCreationOptions.queryOptions instanceof Signal,
                queryOptions:
                    tableCreationOptions.queryOptions instanceof Signal
                        ? tableCreationOptions.queryOptions.value
                        : tableCreationOptions.queryOptions,
            });
        }

        const devtoolsName = tableCreationOptions.devtoolsName ?? this.#pendingDevtoolsName;
        const devtoolsInstanceName = devtoolsName
            ? `TableModel: ${devtoolsName} [${this.#id}]`
            : `TableModel [${this.#id}]`;
        this.#devtools = connectReducerDevtools({ name: devtoolsInstanceName });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        // Always use a signal internally - either the one passed in or create one
        this.#queryOptionsSignal =
            tableCreationOptions.queryOptions instanceof Signal
                ? tableCreationOptions.queryOptions
                : signal(tableCreationOptions.queryOptions);

        // Initialize query options in state with wrapped queryFn for optimization
        const initialOptions = this.#createOptimizedQueryOptions(this.#queryOptionsSignal.value);
        this.#takeStateAction({ type: 'updateQueryOptions', queryOptions: initialOptions });
        this.#query = new QueryObserver(this.queryClient, initialOptions);

        // Set up effect for reactive updates - always update QueryObserver with wrapped queryFn
        this.disposableStack.defer(
            effect(() => {
                const newOptions = this.#createOptimizedQueryOptions(this.#queryOptionsSignal.value);
                untracked(() => {
                    if (TRACE) {
                        this.#log.trace({ message: 'Query options changed', queryOptions: newOptions });
                    }
                    this.#takeStateAction({ type: 'updateQueryOptions', queryOptions: newOptions });

                    // Always update QueryObserver - the wrapped queryFn handles optimization
                    if (this.#query) {
                        this.#query.setOptions(newOptions);
                    }
                });
            }),
        );

        // Arbitrary default value; every location in the product overrides this so it doesn't matter.
        this.#bounds = tableCreationOptions.initBounds || { firstRow: 0, windowSize: 100 };
        this.#throttle = tableCreationOptions.initThrottle;

        const unsubscribe = this.#query.subscribe((queryResult) => {
            if (!queryResult.isEnabled) {
                if (TRACE) {
                    this.#log.trace({ message: 'Query disabled, clearing table client' });
                }
                this.#takeStateAction({ type: 'clearTableClient' });
                this.#failureCountSignal.value = 0;
                return;
            }

            if (queryResult.failureCount !== this.#failureCountSignal.value) {
                if (TRACE) {
                    this.#log.trace({
                        message: 'Query failure count changed',
                        previousFailureCount: this.#failureCountSignal.value,
                        failureCount: queryResult.failureCount,
                    });
                }
                this.#failureCountSignal.value = queryResult.failureCount;
            }

            if (queryResult.isPending) {
                if (TRACE && this.#state.tableStatus !== 'connecting') {
                    this.#log.trace({ message: 'Query pending, entering connecting state' });
                }
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'connecting',
                });
                return;
            }

            if (!queryResult.isSuccess) {
                this.#log.error({
                    message: 'Table creation failed',
                    error: queryResult.error ?? undefined,
                    queryResult,
                });

                // Put table into error state which the UI can read
                this.#takeStateAction({
                    type: 'enterErroredState',
                    error: queryResult.error ?? new Error('Unknown Table Creation error'),
                });
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'disconnected',
                });
                return;
            }

            // How does this get reached? ... for now we'll re-use error state from above
            if (!queryResult.data) {
                this.#log.error({
                    message: 'Table creation resulted in no data',
                    error: queryResult.error ?? undefined,
                    queryResult,
                });
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'disconnected',
                });

                this.#takeStateAction({ type: 'clearTableClient' });
                return;
            }

            const { data } = queryResult;

            if (this.#state.tableClient?.key === stringifyKey(data.key)) {
                this.#takeStateAction({ type: 'updateTableConnectionStatus', status: 'connected' });
            }

            if (
                // No client, create a new one
                !this.#state.tableClient ||
                // Recycle the existing client for a new one since the key has changed
                this.#state.tableClient.key !== stringifyKey(data.key)
            ) {
                this.#log.debug({
                    message: 'Creating new TableClient',
                    key: stringifyKey(data.key),
                });
                this.#takeStateAction({
                    type: 'useNewTableCreationResult',
                    tableCreationResult: data,
                    rawClient: this.proxyClientService.client,
                    logger: this.#log,
                });

                // TODO: Monitor keys list
                this.#takeStateAction({
                    type: 'updateTableConnectionStatus',
                    status: 'connected',
                });

                const tc = this.#state.tableClient!;

                if (TRACE) {
                    this.#log.trace({
                        message: 'Setting bounds and throttle on new TableClient',
                        bounds: this.#bounds,
                        throttle: this.#throttle,
                        tableClientKey: tc.key,
                    });
                }

                tc.bounds = this.#bounds;
                tc.throttle = this.#throttle === undefined ? DEFAULT_THROTTLE_MS : this.#throttle;

                return;
            }

            //this.#takeStateAction({ type: 'clearTableClient' });
        });
        this.disposableStack.defer(unsubscribe);

        this.disposableStack.defer(() => {
            if (this.#state.tableClient) {
                this.#state.tableClient[Symbol.dispose]();
            }
        });
    }

    get key() {
        return this.#state.tableClient?.key ?? null;
    }

    get serverUUID() {
        return this.#state.serverUUID;
    }

    #id: string;
    #log: ReactBindings['Logger'];
    #devtools: ReducerDevtoolsConnection<TableState, TableStateAction> | undefined;
    #pendingDevtoolsName: string | undefined;

    #query!: QueryObserver<FullKeyType | OptimizedResultWrapper | undefined, any, FullKeyType | undefined>;

    setDevtoolsName(name: string) {
        if (this.#devtools) {
            this.#devtools.setName(`TableModel: ${name} [${this.#id}]`);
        } else {
            this.#pendingDevtoolsName = name;
        }
    }

    // TODO: Allow bounds to be set to undefined
    setBounds(bounds: Bounds) {
        this.#bounds = bounds;
        if (this.#state.tableClient) {
            this.#state.tableClient.bounds = bounds;
        }
    }

    setThrottle(throttle: number | undefined, getMoreData: boolean): void {
        this.#throttle = throttle;
        if (this.#state.tableClient) {
            this.#state.tableClient.throttle = throttle === undefined ? DEFAULT_THROTTLE_MS : throttle;

            if (getMoreData) {
                this.#state.tableClient.getMoreData();
            }
        }
    }

    async snapshot({ firstRow, windowSize }: { firstRow?: number; windowSize?: number }) {
        if (this.#state.tableCreationResult === null) {
            return null;
        }

        const { key, tableCookie } = this.#state.tableCreationResult;

        if (!tableCookie) {
            return null;
        }

        return getSnapshot(key, tableCookie, firstRow, windowSize);
    }

    async waitForTableConnection(): Promise<void> {
        using stack = new DisposableStack();

        const dataResolved = deferred<void>();

        stack.defer(
            effect(() => {
                if (this.connectionStatus.value === 'connected') {
                    dataResolved.resolve();
                }
            }),
        );

        const timeout = new Promise<never>((_resolve, reject) => {
            stack.adopt(
                setTimeout(() => {
                    reject(new Error(`Timed out waiting for table connection`));
                }, 10_000),
                clearTimeout,
            );
        });

        return await Promise.race([dataResolved.promise, timeout]);
    }

    get sortable() {
        if (this.#state.tableCreationResult?.key) {
            return this.#state.tableCreationResult.key.ex !== 'M' && this.#state.tableCreationResult.key.ex !== 'S';
        }
        return false;
    }

    #state: TableState = {
        columns: [],
        error: undefined,
        isAmendableSortTableCreationResult: false,
        isAmendableFilterTableCreationResult: false,
        queryOptions: queryOptions({ queryKey: ['EMPTY_TABLE'], queryFn: () => null }),
        tableClient: null,
        tableCreationResult: null,
        proxyStatus: 'uninitialized',
        tableStatus: 'uninitialized',
        serverUUID: null,
    };

    private get state() {
        return this.#state;
    }

    @reacts private set state(state: TableState) {
        const previousState = this.#state;
        this.#state = state;

        // Sync the column lookup Map
        if (state.columns.length === 0) {
            this.#columnByIdMap.clear();
        } else if (state.columns !== previousState.columns) {
            this.#columnByIdMap = new Map(state.columns.map((column) => [column.id, column]));
        }

        if (this.#columnsSignal.value !== state.columns) {
            this.#columnsSignal.value = state.columns;
        }

        const nextKey = state.tableClient?.key ?? '';
        if (this.#keySignal.value !== nextKey) {
            this.#keySignal.value = nextKey;
        }

        if (previousState.tableClient !== state.tableClient) {
            if (previousState.tableClient) {
                previousState.tableClient[Symbol.dispose]();
            }

            // Store the mapping of TableClient to QueryOptions
            if (state.tableClient) {
                this.#tableClientQueryOptionsMap.set(state.tableClient, state.queryOptions);
            }
        }

        // Update the stored query options mapping when queryOptions change
        // Note: QueryObserver.setOptions is called from init() effect, and the
        // wrapped queryFn handles sort/filter optimization at fetch time
        if (previousState.queryOptions !== state.queryOptions && state.tableClient) {
            this.#tableClientQueryOptionsMap.set(state.tableClient, state.queryOptions);
        }
    }

    get tableClient() {
        return this.#state.tableClient;
    }

    #takeStateAction(action: TableStateAction) {
        const nextState = tableStateReducer(this.state, action);
        if (nextState !== this.state) {
            this.state = nextState;
            this.#updateConnectionStatusSignal();
            this.#devtools?.send(action, this.#state);
        }
    }

    #throttle: number | undefined;

    get throttle() {
        return this.#throttle;
    }

    #updateConnected$ = new BehaviorSubject<RowUpdate | typeof NEW_TABLE_RESET>(NEW_TABLE_RESET);
    #updatesConnected$: Connectable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET>;
    #update$: TableClient['update$'];
    #updates$: TableClient['updates$'];

    get update$(): TableClient['update$'] {
        return this.#update$;
    }

    get updates$(): TableClient['updates$'] {
        return this.#updates$;
    }

    #updateSignal!: Signal<SignalUpdate>;
    #countSignal!: Signal<number>;
    get updateSignal(): ReadonlySignal<SignalUpdate> {
        if (this.#updateSignal) return this.#updateSignal;

        this.#updateSignal = signal<SignalUpdate>({
            update: this.#updateConnected$.value === NEW_TABLE_RESET ? undefined : this.#updateConnected$.value,
        });

        let update: SignalUpdate = { update: undefined };
        let lastUpdate: SignalUpdate = { update: undefined };

        this.disposableStack.use(
            this.#updateConnected$.pipe(skip(1)).subscribe((tableUpdate) => {
                const tmp = update;
                update = lastUpdate;
                lastUpdate = tmp;

                update.update = tableUpdate === NEW_TABLE_RESET ? undefined : tableUpdate;

                this.#updateSignal.value = update;
            }),
        );

        return this.#updateSignal;
    }

    get countSignal(): ReadonlySignal<number> {
        if (this.#countSignal) return this.#countSignal;

        // Initialize with 0 since count$ starts with NEW_TABLE_RESET
        this.#countSignal = signal<number>(0);

        this.disposableStack.use(
            this.#countConnected$.subscribe((count) => {
                this.#countSignal.value = (count as any) === NEW_TABLE_RESET ? 0 : count;
            }),
        );

        return this.#countSignal;
    }

    get updateWithReset$(): Observable<ObservedValueOf<TableClient['update$']> | NEW_TABLE_RESET> {
        return this.#updateConnected$;
    }

    get updatesWithReset$(): Observable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET> {
        return this.#updatesConnected$;
    }

    /**
     * @deprecated Prefer the Signal form of `queryOptions` in `init()` for reactivity.
     * This imperative API exists for legacy code that cannot easily adopt signals.
     */
    updateQueryOptions(queryOptions: QueryObserverOptions<any, any, FullKeyType>) {
        if (TRACE) {
            this.#log.trace({
                message: 'updateQueryOptions called directly',
                queryOptions,
            });
        }
        // Simply update the signal - the effect handles everything else
        this.#queryOptionsSignal.value = queryOptions;
    }
}
